import _, {find} from 'lodash';
import {
  convertRowToNumeric,
  getHighlightedWell,
  getHighlightedWellFromTask,
  isLabware384,
  isLabwareTrough,
  isTipCountInLabwareValid,
} from 'contents/ProtocolEditor/VirtualTray/labwareHelper';
import WellItem from 'models/wellItem';
import {PipettingParameterKeysByTaskType, SERIAL_DILUTION_RAW_TASK_PROPERTIES} from '../constants/cardStateEnum';
import {SwipeSelectionDirection, SerialDilutionStep, TaskType} from '../constants/dataEnum';
import {cloneAndMapObject, mapObject} from './objectHelper';

export const isSerialDilutionUses2Quadrants = (sdRawTasks, sdRawTasksSummary) => {
  const {taskSampleTarget: sampleTargetSummary, taskDiluentTargets: diluentTargetsSummary} = sdRawTasksSummary;
  const firstStepDiluent = sdRawTasks[diluentTargetsSummary[0]];
  const secondStepDiluent = sdRawTasks[diluentTargetsSummary[1]];

  if (sampleTargetSummary.length > 1) return true;

  if (secondStepDiluent && firstStepDiluent?.step) {
    return firstStepDiluent.step === secondStepDiluent.step;
  }

  return false;
};

const getRawTasks = (mappedProcessedTasks, rawTasksSummary, isUsing2Quadrants) => {
  const rawTasks = {};

  Object.keys(mappedProcessedTasks).some((sdStep) => {
    const rawID = rawTasksSummary[sdStep];
    const stepNumber = Number(sdStep);
    const processedTask = mappedProcessedTasks[sdStep];

    if (!processedTask) return false;

    if (_.isArray(processedTask)) {
      let diluentStep = 0;

      rawID.forEach((id, index) => {
        const rawDiluentId = id;
        const diluentTask = processedTask[index];

        // for diluent target that uses 2 quadrants, the odd number is quadrant 1, even is quadrant 2
        const isOddIndex = (index + 1) % 2 !== 0;
        if ((isOddIndex && isUsing2Quadrants) || !isUsing2Quadrants) diluentStep += 1;

        rawTasks[rawDiluentId] = cloneAndMapObject(
          {
            ...diluentTask,
            ID: rawDiluentId,
            processedTaskId: diluentTask.ID,
            number: stepNumber,
          },
          [...SERIAL_DILUTION_RAW_TASK_PROPERTIES, ...PipettingParameterKeysByTaskType[diluentTask.type]],
        );

        if (stepNumber === SerialDilutionStep.SET_DILUENT_TARGET) {
          rawTasks[rawDiluentId].step = diluentTask.step || diluentStep;
        }
      });
    } else {
      const {type: taskType} = processedTask;

      rawTasks[rawID] = cloneAndMapObject(
        {
          ...processedTask,
          ID: rawID,
          processedTaskId: processedTask.ID,
          number: stepNumber,
          type:
            // to fix the former serial dilution's sdRawTask format
            taskType === TaskType.DISPENSE && sdStep === SerialDilutionStep.SET_SAMPLE_TARGET
              ? TaskType.ASPIRATE
              : taskType,
        },
        [...SERIAL_DILUTION_RAW_TASK_PROPERTIES, ...PipettingParameterKeysByTaskType[taskType]],
      );
    }

    return false;
  });

  return rawTasks;
};

const getRawTasksSummaryByStep = (mappedProcessedTasks, numberOfStep, isOrdered) => {
  let orderedRawTaskIndex = 1;

  const diluentSourceTask = mappedProcessedTasks[SerialDilutionStep.SET_DILUENT_SOURCE];
  const sampleSourceTask = mappedProcessedTasks[SerialDilutionStep.SET_SAMPLE_SOURCE];
  const sampleTargetTask = mappedProcessedTasks[SerialDilutionStep.SET_SAMPLE_TARGET];
  const diluentTargetsTask = mappedProcessedTasks[SerialDilutionStep.SET_DILUENT_TARGET];

  if (!isOrdered) {
    return {
      taskDiluentSource: diluentSourceTask?.ID,
      taskSampleSource: sampleSourceTask?.ID,
      taskSampleTarget: sampleTargetTask ? sampleTargetTask.map((task) => task?.ID) : null,
      taskDiluentTargets: diluentTargetsTask ? diluentTargetsTask.map((task) => task?.ID) : null,
    };
  }

  return {
    taskDiluentSource: diluentSourceTask ? orderedRawTaskIndex++ : null,
    taskSampleSource: sampleSourceTask ? orderedRawTaskIndex++ : null,
    taskSampleTarget: sampleTargetTask ? sampleTargetTask.map(() => orderedRawTaskIndex++) : null,
    taskDiluentTargets: [...Array((Number(numberOfStep) || 0) * sampleTargetTask.length).keys()].map(
      () => orderedRawTaskIndex++,
    ),
  };
};

export const mapToSerialDilutionStep = (diluentSource, sampleSource, sampleConcentration, diluentTarget) => ({
  [SerialDilutionStep.SET_DILUENT_SOURCE]: diluentSource,
  [SerialDilutionStep.SET_SAMPLE_SOURCE]: sampleSource,
  [SerialDilutionStep.SET_SAMPLE_TARGET]: sampleConcentration,
  [SerialDilutionStep.SET_DILUENT_TARGET]: diluentTarget,
});

const orderDiluentTargetPattern = (diluentTargetRawTasks) => {
  if (!diluentTargetRawTasks[0]) return diluentTargetRawTasks;

  const isRowBased = diluentTargetRawTasks[0].rowTipCount >= diluentTargetRawTasks[0].columnTipCount;
  const isStepPropertiesExist = Boolean(diluentTargetRawTasks[0].step);

  let isOrderedBasedOnLocation;
  let isOrderedBasedOnStep;

  if (diluentTargetRawTasks.length > 1) {
    isOrderedBasedOnStep = isStepPropertiesExist && diluentTargetRawTasks[0]?.step === diluentTargetRawTasks[1]?.step;

    isOrderedBasedOnLocation = isRowBased
      ? diluentTargetRawTasks[0].row !== diluentTargetRawTasks[1].row
      : diluentTargetRawTasks[0].column !== diluentTargetRawTasks[1].column;
  }

  const isOrdered = isOrderedBasedOnStep || isOrderedBasedOnLocation;

  const median = diluentTargetRawTasks.length / 2;

  const isUsing2Quadrants =
    diluentTargetRawTasks.length % 2 === 0 ||
    (!isOrderedBasedOnStep &&
      diluentTargetRawTasks[median] &&
      diluentTargetRawTasks[0].step === diluentTargetRawTasks[median].step);

  if (!isUsing2Quadrants || isOrdered) return diluentTargetRawTasks;

  let comparedProperties = isRowBased ? 'column' : 'row';
  if (isStepPropertiesExist) comparedProperties = 'step';

  return diluentTargetRawTasks.toSorted((curr, next) => curr[comparedProperties] - next[comparedProperties]);
};

/**
 * function to generate ordered serial dilution's raw tasks and summaries.
 * @param {*} mappedProcessedTasks object of processed tasks that has been mapped by SD step as key with function mapToSerialDilutionStep
 * @param {*} numberOfStep number of diluent targets' step
 * @returns object of ordered sdRawTasksSummary and sdRawTasks
 */
export const generateSdRawTasksAndSummary = (mappedProcessedTasks, numberOfStep) => {
  const initialSdSdRawTasksSummary = getRawTasksSummaryByStep(mappedProcessedTasks, numberOfStep);
  const orderedSdRawTasksSummary = getRawTasksSummaryByStep(mappedProcessedTasks, numberOfStep, true);

  const mappedRawTasksSummary = mapToSerialDilutionStep(
    orderedSdRawTasksSummary.taskDiluentSource,
    orderedSdRawTasksSummary.taskSampleSource,
    orderedSdRawTasksSummary.taskSampleTarget,
    orderedSdRawTasksSummary.taskDiluentTargets,
  );

  const orderedDiluentTask = orderDiluentTargetPattern(mappedProcessedTasks[SerialDilutionStep.SET_DILUENT_TARGET]);
  const diluentTargetRawTasks = mapObject(orderedDiluentTask, (task) => ({[task.ID]: task}));

  const isUsing2Quadrants = isSerialDilutionUses2Quadrants(diluentTargetRawTasks, initialSdSdRawTasksSummary);

  const sdRawTasks = getRawTasks(
    {...mappedProcessedTasks, [SerialDilutionStep.SET_DILUENT_TARGET]: orderedDiluentTask},
    mappedRawTasksSummary,
    isUsing2Quadrants,
  );

  return {sdRawTasksSummary: orderedSdRawTasksSummary, sdRawTasks};
};

export const isChangingSDWellCountAllowed = (sdStep, currentSelectedCard, labwareState) => {
  if (!sdStep || !currentSelectedCard) {
    return false;
  }

  const {sdRawTasksSummary, sdRawTasks} = currentSelectedCard;

  const taskDiluentSourceId = sdRawTasksSummary?.taskDiluentSource;
  const taskSampleSourceId = sdRawTasksSummary?.taskSampleSource;
  const taskSampleTargetId = sdRawTasksSummary?.taskSampleTarget && sdRawTasksSummary?.taskSampleTarget[0];
  const taskDiluentTargets = sdRawTasksSummary?.taskDiluentTargets ?? [];

  const taskDiluentSourceLabwareId = sdRawTasks[taskDiluentSourceId]?.labwareID ?? null;
  const taskSampleSourceLabwareId = sdRawTasks[taskSampleSourceId]?.labwareID ?? null;

  const taskDiluentSourceLabware = find(labwareState, {LabwareID: taskDiluentSourceLabwareId});
  const taskSampleSourceLabware = find(labwareState, {LabwareID: taskSampleSourceLabwareId});

  const taskDiluentSourceIsTrough = taskDiluentSourceLabwareId ? isLabwareTrough(taskDiluentSourceLabware) : false;
  const taskSampleSourceIsTrough = taskSampleSourceLabwareId ? isLabwareTrough(taskSampleSourceLabware) : false;

  let isDiluentSourceAllowed = false;
  let isSampleSourceAllowed = false;
  let isSampleTargetAllowed = false;
  let isDiluentTargetAllowed = false;

  if (
    sdStep === SerialDilutionStep.SET_DILUENT_SOURCE ||
    (sdStep !== SerialDilutionStep.SET_DILUENT_SOURCE &&
      ((taskDiluentSourceId && taskDiluentSourceIsTrough) || !taskDiluentSourceId))
  ) {
    isDiluentSourceAllowed = true;
  }

  if (
    sdStep === SerialDilutionStep.SET_SAMPLE_SOURCE ||
    (sdStep !== SerialDilutionStep.SET_SAMPLE_SOURCE &&
      ((taskSampleSourceId && taskSampleSourceIsTrough) || !taskSampleSourceId))
  ) {
    isSampleSourceAllowed = true;
  }

  if (
    sdStep === SerialDilutionStep.SET_SAMPLE_TARGET ||
    (sdStep !== SerialDilutionStep.SET_SAMPLE_TARGET && !taskSampleTargetId)
  ) {
    isSampleTargetAllowed = true;
  }

  if (
    taskDiluentTargets.length < 1 &&
    (sdStep === SerialDilutionStep.SET_DILUENT_TARGET || sdStep !== SerialDilutionStep.SET_DILUENT_TARGET)
  ) {
    isDiluentTargetAllowed = true;
  }

  return isDiluentSourceAllowed && isSampleSourceAllowed && isSampleTargetAllowed && isDiluentTargetAllowed;
};

export const SerialDilutionLabwareValidator = (labware, well = new WellItem()) => {
  const validateTipCount = (isMultiQuadrant) => {
    const is384 = isLabware384(labware);

    const is2DWellSelectionValid = isMultiQuadrant ? is384 : true;
    const isTipCountValid = isTipCountInLabwareValid(labware, well.rowTipCount || 1, well.columnTipCount || 1);

    return is2DWellSelectionValid && isTipCountValid;
  };

  const validateLabware = (sdStep) => {
    const {isIFC, isSpearBio} = labware;

    const isTrough = isLabwareTrough(labware);

    const isLabwareAllowedForSD = !isIFC && !isSpearBio;
    const isLawbwareAllowedForSDStep = isTrough
      ? sdStep === SerialDilutionStep.SET_SAMPLE_SOURCE || sdStep === SerialDilutionStep.SET_DILUENT_SOURCE
      : true;

    return isLabwareAllowedForSD && isLawbwareAllowedForSDStep;
  };

  const validateTaskIntersection = (sdStep, rawTasks, skipWellMode) => {
    const {LabwareID: labwareId} = labware;
    const {rowTipCount, columnTipCount, column, row} = well;

    const sameLabwareRawTasks = Object.values(rawTasks || {}).filter(
      (task) => task.labwareID === labwareId && task.number !== sdStep,
    );

    const highlightedWell = getHighlightedWellFromTask(sameLabwareRawTasks, skipWellMode);
    const wellFromTask = getHighlightedWell(row, column, rowTipCount, columnTipCount, skipWellMode);
    const isWellIntersectedWithExistingTask = wellFromTask.some((well) => highlightedWell.includes(well));

    return isWellIntersectedWithExistingTask;
  };

  return {
    validateTipCount,
    validateLabware,
    validateTaskIntersection,
  };
};

/**
 * Restricts well swipe for serial dilution based on existing SD tasks on the labware so they won't collide.
 *
 * @param {Object} params - Object of parameters
 * @param {Array} params.rawTasksOnLabware - The list of existing raw tasks on the labware.
 * @param {Object} params.swipingInfo - Swiping information
 * @param {Object} [params.wellData=new WellItem()] - Data about the well being interacted with.
 * @param {boolean} [params.skipWellMode=false] - Whether to skip well mode.
 * @param {boolean} [params.is384=false] - Whether the labware is a 384-well plate.
 * @returns {Array} - Array of [row, column] after the well has been processed
 */
export const restrictSDSwipeByExistingTasks = ({
  rawTasksOnLabware,
  swipingInfo,
  wellData = new WellItem(),
  skipWellMode = false,
  is384 = false,
}) => {
  const {row, column, rowTipCount, columnTipCount} = wellData;
  const {swipingStart} = swipingInfo;

  const newRowTipCount = rowTipCount * (is384 ? 2 : 1);
  const newColumnTipCount = columnTipCount * (is384 ? 2 : 1);

  const actualRowSize = Math.abs(row - swipingStart.row) + (swipingStart.row > row ? 0 : 1);
  const actualColumnSize = Math.abs(column - swipingStart.column) + (swipingStart.column > column ? 0 : 1);

  let rowSize = Math.ceil(actualRowSize / newRowTipCount) * newRowTipCount;
  let columnSize = Math.ceil(actualColumnSize / newColumnTipCount) * newColumnTipCount;

  const startRow = swipingStart.row <= row ? swipingStart.row : swipingStart.row - rowSize;
  const startColumn = swipingStart.column <= column ? swipingStart.column : swipingStart.column - columnSize;

  if (swipingStart.row > row) {
    rowSize += newRowTipCount;
  }

  if (swipingStart.column > column) {
    columnSize += newColumnTipCount;
  }

  const highlightedWell = getHighlightedWell(startRow, startColumn, rowSize, columnSize, skipWellMode);

  // get restrictedTask
  let barrierTask;

  // swiping left
  if (swipingStart.row < row && rowTipCount === 1) {
    const sortedTasks =
      rawTasksOnLabware
        .filter((task) => {
          const highlightedWellBarrierTask = getHighlightedWellFromTask([task], skipWellMode);
          const isIntersectionColumn = highlightedWellBarrierTask.some((well) =>
            highlightedWell.find((e) => well.split('-')[1] === e.split('-')[1]),
          );

          return convertRowToNumeric(task.row) > swipingStart.row && isIntersectionColumn;
        })
        .sort((task1, task2) => (task1.row < task2.row ? -1 : 0)) ?? [];

    [barrierTask] = sortedTasks;
  }

  // swiping right
  if (swipingStart.row > row && rowTipCount === 1) {
    const sortedTasks = rawTasksOnLabware
      .filter((task) => {
        const highlightedWellBarrierTask = getHighlightedWellFromTask([task], skipWellMode);
        const isIntersectionColumn = highlightedWellBarrierTask.some((well) =>
          highlightedWell.find((e) => well.split('-')[1] === e.split('-')[1]),
        );

        return convertRowToNumeric(task.row) < swipingStart.row && isIntersectionColumn;
      })
      .sort((task1, task2) => convertRowToNumeric(task2.row) - convertRowToNumeric(task1.row));
    [barrierTask] = sortedTasks;
  }

  // swiping down
  if (swipingStart.column < column && columnTipCount === 1) {
    const sortedTasks = rawTasksOnLabware
      .filter((task) => {
        const highlightedWellBarrierTask = getHighlightedWellFromTask([task], skipWellMode);
        const isIntersectionRow = highlightedWellBarrierTask.some((well) =>
          highlightedWell.find((e) => well.split('-')[0] === e.split('-')[0]),
        );

        return task.column > swipingStart.column && isIntersectionRow;
      })
      .sort((task1, task2) => task1.column - task2.column);
    [barrierTask] = sortedTasks;
  }

  // swiping up
  if (swipingStart.column > column && columnTipCount === 1) {
    const sortedTasks = rawTasksOnLabware
      .filter((task) => {
        const highlightedWellBarrierTask = getHighlightedWellFromTask([task], skipWellMode);
        const isIntersectionRow = highlightedWellBarrierTask.some((well) =>
          highlightedWell.find((e) => well.split('-')[0] === e.split('-')[0]),
        );

        return task.column < swipingStart.column && isIntersectionRow;
      })
      .sort((task1, task2) => task2.column - task1.column);
    [barrierTask] = sortedTasks;
  }

  let newRow = row;
  let newColumn = column;

  if (barrierTask) {
    const highlightedWellBarrierTask = getHighlightedWellFromTask([barrierTask], skipWellMode);

    const isIntersectionRow = highlightedWellBarrierTask.some((well) =>
      highlightedWell.find((e) => well.split('-')[0] === e.split('-')[0]),
    );
    const isIntersectionColumn = highlightedWellBarrierTask.some((well) =>
      highlightedWell.find((e) => well.split('-')[1] === e.split('-')[1]),
    );

    const barrierTaskRow = convertRowToNumeric(barrierTask.row);
    const minBarrierRow = barrierTaskRow;
    const maxBarrierRow = barrierTaskRow + newRowTipCount - (is384 ? 2 : 1);
    const minBarrierColumn = barrierTask.column;
    const maxBarrierColumn = barrierTask.column + newColumnTipCount - (is384 ? 2 : 1);

    // swiping left
    if (swipingStart.row < row) {
      const mostLeftRow =
        swipingStart.row - 1 + Math.ceil((row - swipingStart.row + 1) / newRowTipCount) * newRowTipCount;

      if (isIntersectionColumn && swipingStart.row < minBarrierRow && minBarrierRow <= mostLeftRow) {
        newRow = minBarrierRow - newRowTipCount;
        if (newRow < swipingStart.row) {
          newRow = swipingStart.row;
        }
      }
    }

    // swiping right
    if (swipingStart.row > row) {
      const mostRightRow = swipingStart.row - Math.ceil((swipingStart.row - row) / newRowTipCount) * newRowTipCount;

      if (isIntersectionColumn && swipingStart.row > maxBarrierRow && maxBarrierRow >= mostRightRow) {
        newRow = maxBarrierRow + newRowTipCount;
        if (newRow > swipingStart.row) {
          newRow = swipingStart.row;
        }
      }
    }

    // swiping down
    if (swipingStart.column < column) {
      const mostDownColumn =
        swipingStart.column - 1 + Math.ceil((column - swipingStart.column + 1) / newColumnTipCount) * newColumnTipCount;
      if (isIntersectionRow && swipingStart.column < minBarrierColumn && minBarrierColumn <= mostDownColumn) {
        // reduce 1 to include the starting swipe column
        newColumn = minBarrierColumn - 1;
        if (newColumn < swipingStart.column) {
          newColumn = swipingStart.column;
        }
      }
    }

    // swiping up
    if (swipingStart.column > column) {
      // highest positioned column is on the current hovered column when the swiping position is upward
      const mostUpColumn = column;

      // if the starting swipe position is below the maxColumn and the current hovered column is above or on the maxColumn well,
      // then limit the column selection to the max allowed column
      if (isIntersectionRow && swipingStart.column > maxBarrierColumn && maxBarrierColumn >= mostUpColumn) {
        // adding 1 to include the starting swipe column
        newColumn = maxBarrierColumn + 1;

        if (newColumn > swipingStart.column) {
          newColumn = swipingStart.column;
        }
      }
    }
  }
  return [newRow, newColumn];
};

export const getSDRawTasksSummaryKeyFromStep = (sdStep) => {
  switch (sdStep) {
    case SerialDilutionStep.SET_SAMPLE_SOURCE:
      return 'taskSampleSource';
    case SerialDilutionStep.SET_DILUENT_SOURCE:
      return 'taskDiluentSource';
    case SerialDilutionStep.SET_SAMPLE_TARGET:
      return 'taskSampleTarget';
    case SerialDilutionStep.SET_DILUENT_TARGET:
      return 'taskDiluentTargets';
    default:
      return null;
  }
};

export const isSDSwipeConvertibleToStamping = (
  swipePattern,
  swipeDirection,
  allowedSwipeDirection = [SwipeSelectionDirection.ALL],
) => {
  const isFirstInitializingWellPattern = !swipePattern.rowTipCount && !swipePattern.columnTipCount;
  const isSwiping2D = swipeDirection.x && swipeDirection.y;

  const isAllowAllDirection = allowedSwipeDirection.includes(SwipeSelectionDirection.ALL);
  const isAllowStamping = isAllowAllDirection || allowedSwipeDirection.includes(SwipeSelectionDirection.STAMPING);

  const isForceStamping = !isAllowAllDirection && isAllowStamping && isSwiping2D;

  return (
    isForceStamping || Boolean(isSwiping2D && isFirstInitializingWellPattern && isAllowAllDirection && isAllowStamping)
  );
};

export const convertSwipePatternToStamping = (swipePattern, is384) => {
  const {row: originalRow, column: originalColumn} = swipePattern;

  const row = is384 && originalRow % 2 === 0 ? 2 : 1;
  const column = is384 && originalColumn % 2 === 0 ? 2 : 1;
  const rowTipCount = 8;
  const columnTipCount = 12;

  const newSwipePattern = new WellItem(row, column, rowTipCount, columnTipCount);

  return newSwipePattern;
};

export const getAdjustedSwipeDirectionByTipCount = (swipePattern, swipeDirection, allowedSwipeDirection) => {
  const {rowTipCount, columnTipCount} = swipePattern;

  const isAllowAllPattern = allowedSwipeDirection.includes(SwipeSelectionDirection.ALL);
  const isAllowedSwipingX = isAllowAllPattern || allowedSwipeDirection.includes(SwipeSelectionDirection.WHOLE_ROW);
  const isAllowedSwipingY = isAllowAllPattern || allowedSwipeDirection.includes(SwipeSelectionDirection.WHOLE_COLUMN);

  const isSingleTipUse = rowTipCount * columnTipCount === 1 || (!rowTipCount && !columnTipCount);

  // for singleTipUse, the reference point will not be decided and instead will be the direction of the swiping.
  // otherwise use the biggest amount of well count row/column as the anchor and nullify the other axis to maintain 1D pattern.
  const isSwipingX = isSingleTipUse ? swipeDirection.x : rowTipCount < columnTipCount;
  const isSwipingY = !isSwipingX;

  const newSwipingDirection = {
    y: isSwipingY && isAllowedSwipingY ? swipeDirection.y : null,
    x: isSwipingX && isAllowedSwipingX ? swipeDirection.x : null,
  };

  return newSwipingDirection;
};

export const getSDAllowedSwipeDirectionOnTrough = (troughsUsed, swipePattern, isDoingDilution) => {
  const biggestTipCountTrough = troughsUsed[0];

  const {rowTipCount: startSwipeRowTipCount, columnTipCount: startSwipeColumnTipCount} = swipePattern;
  const {rowCount: troughRowCount, colCount: troughColCount} = biggestTipCountTrough;

  const isPatternEstablished = startSwipeRowTipCount && startSwipeColumnTipCount;
  const isSelectingAllWell = startSwipeRowTipCount === troughRowCount && startSwipeColumnTipCount === troughColCount;
  const isSingleWellTrough = troughRowCount * troughColCount === 1;

  if (isPatternEstablished) {
    if (isDoingDilution) return [SwipeSelectionDirection.ALL];
    if (isSelectingAllWell) return [SwipeSelectionDirection.STAMPING];

    return [];
  }

  const allowedPattern =
    biggestTipCountTrough.rowCount < biggestTipCountTrough.colCount
      ? [SwipeSelectionDirection.WHOLE_ROW]
      : [SwipeSelectionDirection.WHOLE_COLUMN];

  if (!isPatternEstablished && isSingleWellTrough) {
    allowedPattern.push(SwipeSelectionDirection.STAMPING);
  }

  return isSingleWellTrough ? [SwipeSelectionDirection.ALL] : allowedPattern;
};

export const mapToSDSwipeData = ({swipeDirection, swipePattern, currentHoveredWell}) => {
  const {row, column, rowTipCount, columnTipCount} = swipePattern;

  const tipCount = {row: rowTipCount, column: columnTipCount};
  const startSwipePosition = {row, column};
  const endSwipePosition = {...startSwipePosition};

  if (swipeDirection.x) {
    endSwipePosition.row = currentHoveredWell.row;
  }

  if (swipeDirection.y) {
    endSwipePosition.column = currentHoveredWell.column;
  }

  return {
    tipCount,
    startSwipePosition,
    endSwipePosition,
  };
};

export const getIsQuadrantSelectionWithReplicateWell = ({labware, replicateWell, columnTipCount, rowTipCount}) => {
  const is384 = isLabware384(labware);

  return is384 && replicateWell > 1 && (rowTipCount > labware.rowCount / 2 || columnTipCount > labware.colCount / 2);
};

export const isSDPatternAllowFreeSelection = (task = {rowTipCount: 0, columnTipCount: 0}, labware = {}) => {
  const {isTrough} = labware || {};
  const {rowTipCount, columnTipCount} = task;

  return rowTipCount === 1 && columnTipCount === 1 && isTrough;
};
